// Layer batch naming. New layer batches are LAY-YY-NN (sheep batches keep the
// lamb "L-" prefix, so "L-26-01" no longer means two things). Batches made
// before the switch are L-YY-NN; both forms count toward the next number, so
// numbering continues across the rename.

const LAYER_BATCH_NAME = /^(?:LAY|L)-\d{2}-(\d{2,})$/;

type NamedBatch = {id?: string | null; name?: string | null};

// The record id a new batch gets from its name (`L-26-01` -> `l-26-01`). A
// renamed batch keeps its original id, so a new name can map onto an id that
// is already taken; callers must check `layerBatchIdTaken` before saving.
export function layerBatchIdFromName(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
}

export function layerBatchIdTaken(name: string, batches: readonly NamedBatch[]): boolean {
  const id = layerBatchIdFromName(name.trim());
  return batches.some((b) => b.id === id || layerBatchIdFromName(String(b.name || '').trim()) === id);
}

// Suggest the next LAY-YY-NN: one past the highest NN of any L-/LAY- batch
// (across all years, as before), skipping any number whose id is taken.
export function nextLayerBatchName(batches: readonly NamedBatch[], now: Date = new Date()): string {
  const yr = now.getFullYear().toString().slice(2);
  let max = 0;
  for (const b of batches) {
    const m = LAYER_BATCH_NAME.exec(String(b.name || '').trim());
    if (m) max = Math.max(max, parseInt(m[1], 10) || 0);
  }
  let n = max + 1;
  let name = `LAY-${yr}-${String(n).padStart(2, '0')}`;
  while (layerBatchIdTaken(name, batches)) {
    n += 1;
    name = `LAY-${yr}-${String(n).padStart(2, '0')}`;
  }
  return name;
}
